import { useCallback, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import {
  PlanKind,
  PlanVersionData,
  restorePlanVersion,
} from 'app/frontend/base/api/plan_versions'
import {
  usePlanVersion,
  usePlanVersions,
} from 'app/frontend/base/config/queries'

type Args = {
  kind: PlanKind
  owner: RaidPlanOwner
  planId?: number
  planQueryKey: unknown[]
  onRestored?: () => void
}

const usePlanVersionPreview = <T extends PlanVersionData>({
  kind,
  owner,
  planId,
  planQueryKey,
  onRestored,
}: Args) => {
  const queryClient = useQueryClient()
  const [historyOpen, setHistoryOpen] = useState(false)
  const [previewVersionId, setPreviewVersionId] = useState<number>()
  const onRestoredRef = useRef(onRestored)
  onRestoredRef.current = onRestored

  const versionsQuery = usePlanVersions(
    kind,
    owner,
    planId,
    historyOpen || previewVersionId != null,
  )
  const detailQuery = usePlanVersion(kind, owner, planId, previewVersionId)

  const versions = versionsQuery.data?.versions ?? []
  const pendingAuthors = versionsQuery.data?.pendingAuthors ?? []
  const currentIndex = versions.findIndex(
    (entry) => entry.id === previewVersionId,
  )

  const open = useCallback((id: number) => {
    setPreviewVersionId(id)
    setHistoryOpen(false)
  }, [])

  const cancel = useCallback(() => {
    setPreviewVersionId(undefined)
  }, [])

  const step = useCallback(
    (delta: number) => {
      if (currentIndex < 0) return
      const next = versions[currentIndex + delta]
      if (next) setPreviewVersionId(next.id)
    },
    [currentIndex, versions],
  )

  const restoreMutation = useMutation({
    mutationFn: async () => {
      if (!planId || previewVersionId == null) {
        throw new Error('Version missing')
      }
      return await restorePlanVersion(kind, owner, planId, previewVersionId)
    },
    onSuccess: async (updated) => {
      queryClient.setQueryData(planQueryKey, updated)
      await queryClient.invalidateQueries({
        queryKey: ['planVersions', { kind, owner, planId }],
      })
      if (kind === 'raid') {
        await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
        await queryClient.invalidateQueries({ queryKey: ['userRaidPlans'] })
      } else {
        await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
      }
    },
  })

  const restore = useCallback(async () => {
    await restoreMutation.mutateAsync()
    onRestoredRef.current?.()
    setPreviewVersionId(undefined)
  }, [restoreMutation])

  return {
    historyOpen,
    setHistoryOpen,
    isActive: previewVersionId != null,
    previewVersionId,
    versions,
    pendingAuthors,
    versionsLoading: versionsQuery.isFetching,
    summary: currentIndex >= 0 ? versions[currentIndex] : undefined,
    versionNumber:
      currentIndex >= 0 ? versions.length - currentIndex : undefined,
    data: detailQuery.data?.data as T | undefined,
    isLoading:
      Boolean(previewVersionId) && detailQuery.isFetching && !detailQuery.data,
    canStepOlder: currentIndex >= 0 && currentIndex < versions.length - 1,
    canStepNewer: currentIndex > 0,
    open,
    cancel,
    step,
    restore,
    isRestoring: restoreMutation.isPending,
  }
}

export default usePlanVersionPreview
